import type { DocMeta } from '@blocksuite/store';
import { toast } from '@pique/component';
import { useTrashModalHelper } from '@pique/core/hooks/pique/use-trash-modal-helper';
import { useBlockSuiteDocMeta } from '@pique/core/hooks/use-block-suite-page-meta';
import { AuthService } from '@pique/core/modules/cloud';
import { CollectionService } from '@pique/core/modules/collection';
import type { Tag } from '@pique/core/modules/tag';
import type { Collection, Filter } from '@pique/env/filter';
import { Trans, useI18n } from '@pique/i18n';
import { useLiveData, useService, WorkspaceService } from '@toeverything/infra';
import { useAtom } from 'jotai';
import { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react';

import { calendarEventsDisplayPreferenceAtom } from '../../pique/setting-modal/atoms';
import { ListFloatingToolbar } from '../components/list-floating-toolbar';
import { usePageItemGroupDefinitions } from '../group-definitions';
import { PageOperationCell } from '../operation-cell';
import { PageListItemRenderer } from '../page-group';
import type { ItemListHandle, ListItem } from '../types';
import { useFilteredPageMetas } from '../use-filtered-page-metas';
import { VirtualizedList } from '../virtualized-list';
import { NotesEmptyHint } from './notes-empty-hint';
import {
  CollectionPageListHeader,
  PageListHeader,
  TagPageListHeader,
} from './page-list-header';

// Lazy import for UpcomingEvents to reduce bundle size
const UpcomingEvents = lazy(() =>
  import('@pique/core/pages/workspace/all-page/upcoming-events').then(
    module => ({ default: module.UpcomingEvents })
  )
);

const usePageOperationsRenderer = () => {
  const t = useI18n();
  const collectionService = useService(CollectionService);
  const removeFromAllowList = useCallback(
    (id: string) => {
      collectionService.deletePagesFromCollections([id]);
      toast(t['com.pique.collection.removePage.success']());
    },
    [collectionService, t]
  );
  const pageOperationsRenderer = useCallback(
    (page: DocMeta, isInAllowList?: boolean) => {
      return (
        <PageOperationCell
          page={page}
          isInAllowList={isInAllowList}
          onRemoveFromAllowList={() => removeFromAllowList(page.id)}
        />
      );
    },
    [removeFromAllowList]
  );
  return pageOperationsRenderer;
};

export const VirtualizedPageList = ({
  tag,
  collection,
  filters,
  listItem,
  setHideHeaderCreateNewPage,
}: {
  tag?: Tag;
  collection?: Collection;
  filters?: Filter[];
  listItem?: DocMeta[];
  setHideHeaderCreateNewPage?: (hide: boolean) => void;
}) => {
  const listRef = useRef<ItemListHandle>(null);
  const [showFloatingToolbar, setShowFloatingToolbar] = useState(false);
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([]);
  const currentWorkspace = useService(WorkspaceService).workspace;
  const pageMetas = useBlockSuiteDocMeta(currentWorkspace.docCollection);
  const pageOperations = usePageOperationsRenderer();

  // Filter pageMetas if no listItem is provided
  const filteredPageMetas = useFilteredPageMetas(pageMetas, {
    filters,
    collection,
  });

  // Use provided listItem or the filtered pageMetas
  const pageMetasToRender = useMemo(() => {
    return listItem || filteredPageMetas;
  }, [listItem, filteredPageMetas]);

  const filteredSelectedPageIds = useMemo(() => {
    const ids = pageMetasToRender.map(page => page.id);
    return selectedPageIds.filter(id => ids.includes(id));
  }, [pageMetasToRender, selectedPageIds]);

  const hideFloatingToolbar = useCallback(() => {
    listRef.current?.toggleSelectable();
  }, []);

  const pageOperationRenderer = useCallback(
    (item: ListItem) => {
      const page = item as DocMeta;
      const isInAllowList = collection?.allowList?.includes(page.id);
      return pageOperations(page, isInAllowList);
    },
    [collection, pageOperations]
  );

  // Calendar events logic - only load when needed
  const authService = useService(AuthService);
  const account = useLiveData(authService.session.account$);
  const isLoggedIn = !!account;
  const [displayPreference] = useAtom(calendarEventsDisplayPreferenceAtom);
  const shouldShowEvents = displayPreference === 'yes' && isLoggedIn;

  const isNotesView = !collection && !tag;
  const showEmptyHint = isNotesView && pageMetasToRender.length === 0;

  const pageHeaderRenderer = useCallback(() => {
    return (
      <>
        {shouldShowEvents && (
          <Suspense
            fallback={<div style={{ height: '1px', minHeight: '1px' }} />}
          >
            <div style={{ minHeight: '1px' }}>
              <UpcomingEvents />
            </div>
          </Suspense>
        )}
        {showEmptyHint && <NotesEmptyHint />}
      </>
    );
  }, [shouldShowEvents, showEmptyHint]);

  const pageItemRenderer = useCallback((item: ListItem) => {
    return <PageListItemRenderer {...item} />;
  }, []);

  const heading = useMemo(() => {
    if (tag) {
      return <TagPageListHeader workspaceId={currentWorkspace.id} tag={tag} />;
    }
    if (collection) {
      return (
        <CollectionPageListHeader
          workspaceId={currentWorkspace.id}
          collection={collection}
        />
      );
    }
    return <PageListHeader />;
  }, [collection, currentWorkspace.id, tag]);

  const { setTrashModal } = useTrashModalHelper(currentWorkspace.docCollection);

  const handleMultiDelete = useCallback(() => {
    if (filteredSelectedPageIds.length === 0) {
      return;
    }
    const pageNameMapping = Object.fromEntries(
      pageMetas.map(meta => [meta.id, meta.title])
    );

    const pageNames = filteredSelectedPageIds.map(
      id => pageNameMapping[id] ?? ''
    );
    setTrashModal({
      open: true,
      pageIds: filteredSelectedPageIds,
      pageTitles: pageNames,
    });
    hideFloatingToolbar();
  }, [filteredSelectedPageIds, hideFloatingToolbar, pageMetas, setTrashModal]);

  const group = usePageItemGroupDefinitions();

  return (
    <>
      <VirtualizedList
        ref={listRef}
        selectable="toggle"
        draggable
        atTopThreshold={80}
        atTopStateChange={setHideHeaderCreateNewPage}
        onSelectionActiveChange={setShowFloatingToolbar}
        heading={heading}
        groupBy={group}
        selectedIds={filteredSelectedPageIds}
        onSelectedIdsChange={setSelectedPageIds}
        items={pageMetasToRender}
        rowAsLink
        docCollection={currentWorkspace.docCollection}
        operationsRenderer={pageOperationRenderer}
        itemRenderer={pageItemRenderer}
        headerRenderer={
          (shouldShowEvents && !collection) || showEmptyHint
            ? pageHeaderRenderer
            : undefined
        }
      />
      <ListFloatingToolbar
        open={showFloatingToolbar}
        onDelete={handleMultiDelete}
        onClose={hideFloatingToolbar}
        content={
          <Trans
            i18nKey="com.pique.page.toolbar.selected"
            count={filteredSelectedPageIds.length}
          >
            <div style={{ color: 'var(--affine-text-secondary-color)' }}>
              {{ count: filteredSelectedPageIds.length } as any}
            </div>
            selected
          </Trans>
        }
      />
    </>
  );
};
